import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Loader2, ArrowLeft, Linkedin, Twitter, Facebook, Link2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared/PageHeader";
import { useSeo } from "@/hooks/useSeo";
import { fetchBlogPost } from "@/lib/contentApi";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined=loading, null=notfound
  useEffect(() => { fetchBlogPost(slug).then(setPost).catch(() => setPost(null)); }, [slug]);
  useSeo(post ? (post.seoTitle || post.title) : "Blog", post ? (post.seoDescription || post.excerpt) : undefined);

  if (post === undefined) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>;
  if (post === null) return <Navigate to="/blog" replace />;

  const url = window.location.href;
  const share = (net) => {
    const map = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    if (net === "copy") { navigator.clipboard.writeText(url); toast.success("Link copied!"); return; }
    window.open(map[net], "_blank", "noopener");
  };

  return (
    <div data-testid="page-blog-detail">
      <PageHeader overline={post.category} titleLines={[post.title]} description={post.excerpt} />
      <section className="bg-white py-16 md:py-24">
        <article className="container-asai max-w-3xl">
          <Link to="/blog" className="mb-8 inline-flex items-center gap-2 text-sm text-navy/60 hover:text-orange"><ArrowLeft className="h-4 w-4" /> Back to Blog</Link>
          <div className="mb-6 flex items-center gap-3 text-sm text-navy/50">
            <span>{post.author || "ASAI Team"}</span><span>·</span><span>{(post.publishDate || "").slice(0, 10)}</span>
          </div>
          {post.featuredImage && <img src={post.featuredImage} alt={post.title} className="mb-8 w-full rounded-2xl" />}
          <div className="prose-asai whitespace-pre-wrap text-lg leading-relaxed text-navy/80" data-testid="blog-content">{post.content}</div>

          <div className="mt-10 flex items-center gap-3 border-t border-navy/10 pt-6">
            <span className="text-sm font-medium text-navy/60">Share:</span>
            {[["linkedin", Linkedin], ["twitter", Twitter], ["facebook", Facebook], ["copy", Link2]].map(([net, Icon]) => (
              <button key={net} onClick={() => share(net)} data-testid={`share-${net}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/15 text-navy/60 transition-colors hover:border-orange hover:text-orange"><Icon className="h-4 w-4" /></button>
            ))}
          </div>

          {post.related?.length > 0 && (
            <div className="mt-14">
              <h2 className="mb-6 font-heading text-2xl text-navy">Related posts</h2>
              <div className="grid gap-6 sm:grid-cols-3">
                {post.related.map((r) => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="group rounded-2xl border border-navy/10 bg-white p-5 transition-all hover:-translate-y-1">
                    <span className="text-xs font-semibold uppercase text-royal">{r.category}</span>
                    <h3 className="mt-2 font-heading text-lg leading-snug text-navy">{r.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
